import { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Divider, Form, Grid, Header, Icon, Label, Loader, Message, Modal, Segment, Step, Table } from "semantic-ui-react";
import { bookingApi, roomTypeApi } from "../../api/api";
import { mapEmployeeBookingHistoryRow } from "../../api/mappers";
import { bookingToCreateForm, fmt } from "./adminHelpers";
import {
  appendBookingPayload, OFFICIAL_ATTACHMENT_ACCEPT, MAX_ATTACHMENT_SIZE, ATTACHMENT_TOO_LARGE_MSG,
} from "../../utils/multipartForm";
import TimeInput12h from "../TimeInput12h";
import StatusBadge from "./StatusBadge";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const STEPS = ["Applicant Details", "Guest Information", "Accommodation & Stay Details"];
const REQUIRED_MSG = "This field is required";
const GREYED_INPUT_STYLE = { backgroundColor: "#eceff1" };
const OTHER_PAYMENT_CODE = "OTHER";

const FIELD_STEP = {
  employee_id: 0,
  applicant_name: 0,
  designation: 0,
  department: 0,
  email: 0,
  mobile: 0,
  guest_name: 1,
  guest_designation: 1,
  guest_mobile: 1,
  guest_address: 1,
  purpose_of_visit: 1,
  official_attachment: 1,
  preferences: 2,
  rooms_required: 2,
  payment_responsibility: 1,
  payment_responsibility_other: 1,
  arrival_date: 2,
  arrival_time: 2,
  departure_date: 2,
  departure_time: 2,
};

const EMPTY_CB = {
  employee_id: "",
  applicant_name: "",
  designation: "",
  department: "",
  email: "",
  mobile: "",
  guest_name: "",
  guest_designation: "",
  guest_mobile: "",
  guest_address: "",
  guest_same_as_applicant: false,
  booking_type: "OFFICIAL",
  purpose_of_visit: "",
  accompanying_persons_count: 0,
  arrival_date: "",
  arrival_time: "12:00",
  departure_date: "",
  departure_time: "10:00",
  rooms_required: 1,
  payment_responsibility: "",
  payment_responsibility_other: "",
  preferences: [
    { guest_house: "", priority: 1 },
    { guest_house: "", priority: 2 },
    { guest_house: "", priority: 3 },
    { guest_house: "", priority: 4 },
  ],
  room_requests_by_preference: { 0: [], 1: [], 2: [], 3: [] },
  official_attachment: null,
};

/* Fields "Reset" should clear — scoped per step so resetting one tab
   doesn't wipe data already filled in on the others. Mirrors FIELD_STEP
   plus the couple of fields that don't carry their own validation error. */
const STEP_FIELDS = {
  0: Object.keys(FIELD_STEP).filter((k) => FIELD_STEP[k] === 0),
  1: [
    ...Object.keys(FIELD_STEP).filter((k) => FIELD_STEP[k] === 1),
    "booking_type",
    "accompanying_persons_count",
    "guest_same_as_applicant",
  ],
  2: Object.keys(FIELD_STEP).filter((k) => FIELD_STEP[k] === 2),
};

const APPLICANT_TO_GUEST_FIELD = {
  applicant_name: "guest_name",
  designation: "guest_designation",
  mobile: "guest_mobile",
};

const BOOKING_TYPE_OPTIONS = [
  { key: "OFFICIAL", text: "Official", value: "OFFICIAL" },
  { key: "NON_OFFICIAL", text: "Non-official", value: "NON_OFFICIAL" },
];

/* Renders "<Label text> * <error message>" on one line instead of Semantic's
   default pointing label under the input — keeps the input box from being
   pushed down when a validation error appears. Plain "blank field" errors
   aren't repeated on every field — a single banner covers that (see
   REQUIRED_MSG below); only specific format/validation messages show here. */
const inlineLabel = (text, message, required = true) => (
  <label>
    {text} {required && <span style={{ color: "#db2828" }}>*</span>}
    {message && message !== REQUIRED_MSG && (
      <span style={{ color: "#9f3a38", fontWeight: 400, marginLeft: 8 }}>
        {message}
      </span>
    )}
  </label>
);

/* Read-only "label / value" cell used in the Preview Form modal. */
const PF = ({ label, value }) => (
  <Grid.Column>
    <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>
      {label}
    </div>
    <div>{value || "—"}</div>
  </Grid.Column>
);

const digitsOnly = (v) => v.replace(/\D/g, "");
const lettersOnly = (v) => v.replace(/[^A-Za-z\s]/g, "");
const MOBILE_RE = /^\d{10}$/;
const EMPLOYEE_ID_RE = /^\d{5,6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALPHA_RE = /^[A-Za-z\s]+$/;

export default function CreateBookingTab({ guestHouses, paymentResponsibilityOptions, isSuperAdmin, onSuccess, prefillBooking }) {
  const [form, setForm] = useState(EMPTY_CB);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /* A GH_ADMIN with exactly one assigned property doesn't need a picker —
     the API already scopes `guestHouses` down to it, so lock the single
     preference to that. GH_ADMINs with more than one assigned guest house
     get the same multi-preference dropdown as super admins (see
     showPreferenceDropdown below), just restricted to their own houses. */
  useEffect(() => {
    if (!isSuperAdmin && guestHouses.length === 1) {
      setForm((f) => ({
        ...f,
        preferences: [{ guest_house: guestHouses[0].id, priority: 1 }],
      }));
    }
  }, [isSuperAdmin, guestHouses]);
  const [notice, setNotice] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [roomTypesByGh, setRoomTypesByGh] = useState({});
  const fetchingGhIdsRef = useRef(new Set());
  const [roomTypesPanelOpen, setRoomTypesPanelOpen] = useState({});

  /* Past bookings for the employee ID being typed, shown underneath the field
     so the admin can see history before creating (TODO #11). Debounced so a
     lookup fires once typing pauses, not on every keystroke — this hits a
     dedicated, capped backend endpoint rather than reusing the full in-memory
     booking list, which is unbounded and defeats the point of a narrow query
     on a weak server. */
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [employeeHistoryLoading, setEmployeeHistoryLoading] = useState(false);
  const [employeeHistoryError, setEmployeeHistoryError] = useState("");
  const debouncedEmployeeId = useDebouncedValue(form.employee_id, 400);

  useEffect(() => {
    if (!EMPLOYEE_ID_RE.test(debouncedEmployeeId)) {
      setEmployeeHistory([]);
      setEmployeeHistoryError("");
      setEmployeeHistoryLoading(false);
      return;
    }
    let ignore = false;
    setEmployeeHistoryLoading(true);
    setEmployeeHistoryError("");
    bookingApi
      .employeeHistory(debouncedEmployeeId)
      .then((rows) => {
        if (ignore) return;
        setEmployeeHistory((rows || []).map(mapEmployeeBookingHistoryRow));
      })
      .catch(() => {
        if (ignore) return;
        setEmployeeHistoryError("Couldn't load this employee's booking history.");
      })
      .finally(() => {
        if (!ignore) setEmployeeHistoryLoading(false);
      });
    return () => { ignore = true; };
  }, [debouncedEmployeeId]);

  /* "Reuse Details" on an indent's detail page hands the whole booking over so
     a repeat visit doesn't have to be retyped. Applied once per booking object
     (the tab is remounted on every reuse, so identity is a reliable guard),
     never on a plain "Create Booking" tab click — AdminDashboard clears the
     prop there. */
  const appliedPrefillRef = useRef(null);
  useEffect(() => {
    if (!prefillBooking || appliedPrefillRef.current === prefillBooking) return;
    appliedPrefillRef.current = prefillBooking;

    const seed = bookingToCreateForm(prefillBooking);
    /* The payment dropdown is filtered by booking type + is_active, so a code
       that has since been retired (or belongs to the other booking type) has
       no option to select. Drop it rather than leave the field looking blank
       while holding a value that would submit. */
    const paymentStillOffered = (paymentResponsibilityOptions || []).some(
      (o) =>
        o.code === seed.payment_responsibility &&
        o.booking_type === seed.booking_type &&
        o.is_active,
    );

    setForm((f) => ({
      ...EMPTY_CB,
      /* Keeps the single-guest-house lock the effect above sets for a GH_ADMIN;
         for everyone else this is still the blank default — room preferences
         are never carried over from the reused indent. */
      preferences: f.preferences,
      ...seed,
      payment_responsibility: paymentStillOffered ? seed.payment_responsibility : "",
      payment_responsibility_other: paymentStillOffered ? seed.payment_responsibility_other : "",
    }));
    setErrors({});
    setNotice(null);
    setActiveStep(0);
  }, [prefillBooking, paymentResponsibilityOptions]);

  /* Fetch room types for every guest house currently referenced by a
     preference, caching per guest-house id so re-selecting doesn't re-fetch. */
  useEffect(() => {
    const ghIds = form.preferences.map((p) => p.guest_house).filter(Boolean);
    ghIds.forEach((ghId) => {
      if (roomTypesByGh[ghId] !== undefined) return;
      if (fetchingGhIdsRef.current.has(ghId)) return;

      fetchingGhIdsRef.current.add(ghId);
      roomTypeApi
        .list(ghId)
        .then((types) => setRoomTypesByGh((prev) => ({ ...prev, [ghId]: types })))
        .catch(() => setRoomTypesByGh((prev) => ({ ...prev, [ghId]: [] })))
        .finally(() => fetchingGhIdsRef.current.delete(ghId));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferences, roomTypesByGh]);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  /* Preview the selected file (image/PDF inline, other types via download
     link) before it's actually submitted with the booking. */
  useEffect(() => {
    if (!form.official_attachment) {
      setAttachmentPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(form.official_attachment);
    setAttachmentPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.official_attachment]);

  const stepFieldKeys = (step) =>
    Object.keys(FIELD_STEP).filter((k) => FIELD_STEP[k] === step);

  /* Steps can be freely navigated in any order — a step with missing
     mandatory fields is flagged with an error icon (see stepHasErrors)
     instead of being blocked. */
  const goToStep = (target) => {
    setActiveStep(target);
    setNotice(null);
  };

  const goNext = () => goToStep(Math.min(activeStep + 1, STEPS.length - 1));
  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  /* Clears only the fields on the currently active step, leaving whatever
     was already filled in on the other steps untouched. */
  const resetCurrentStep = () => {
    const fields = STEP_FIELDS[activeStep] || [];
    setForm((f) => {
      const next = { ...f };
      fields.forEach((k) => {
        if (k === "preferences") {
          next.preferences =
            !isSuperAdmin && guestHouses.length === 1
              ? [{ guest_house: guestHouses[0].id, priority: 1 }]
              : EMPTY_CB.preferences;
        } else {
          next[k] = EMPTY_CB[k];
        }
      });
      if (activeStep === 2) {
        next.room_requests_by_preference = EMPTY_CB.room_requests_by_preference;
      }
      return next;
    });
    setErrors((e) => {
      const next = { ...e };
      stepFieldKeys(activeStep).forEach((k) => {
        next[k] = undefined;
      });
      return next;
    });
    setNotice(null);
  };

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };

      if (k === "guest_same_as_applicant" && v) {
        Object.entries(APPLICANT_TO_GUEST_FIELD).forEach(([applicantKey, guestKey]) => {
          next[guestKey] = next[applicantKey];
        });
      }

      if (f.guest_same_as_applicant && APPLICANT_TO_GUEST_FIELD[k]) {
        next[APPLICANT_TO_GUEST_FIELD[k]] = v;
      }

      return next;
    });

    const errorKeysToClear =
      k === "guest_same_as_applicant" && v ? [k, ...Object.values(APPLICANT_TO_GUEST_FIELD)] : [k];
    setErrors((e) => {
      const next = { ...e };
      errorKeysToClear.forEach((key) => { next[key] = undefined; });
      return next;
    });
    setNotice(null);
  };

  /* Preferences */
  const setPref = (i, ghId) => {
    setForm((f) => ({
      ...f,
      preferences: f.preferences.map((p, j) =>
        j === i ? { ...p, guest_house: Number(ghId) || "" } : p,
      ),
      room_requests_by_preference: {
        ...f.room_requests_by_preference,
        [i]: [],
      },
    }));
    setErrors((e) => ({ ...e, preferences: undefined }));
    setNotice(null);
  };

  const getRoomRequestCount = (slotIndex, roomTypeId) => {
    const entry = (form.room_requests_by_preference[slotIndex] || []).find(
      (r) => r.roomTypeId === roomTypeId,
    );
    return entry ? String(entry.count) : "";
  };

  const getSlotRoomTotal = (slotIndex) =>
    (form.room_requests_by_preference[slotIndex] || []).reduce(
      (sum, r) => sum + (Number(r.count) || 0),
      0,
    );

  const setRoomRequestCount = (slotIndex, roomTypeId, rawValue) => {
    const count = Number(rawValue);
    setForm((f) => {
      const currentList = f.room_requests_by_preference[slotIndex] || [];
      const withoutType = currentList.filter((r) => r.roomTypeId !== roomTypeId);
      const nextList = count > 0 ? [...withoutType, { roomTypeId, count }] : withoutType;
      return {
        ...f,
        room_requests_by_preference: { ...f.room_requests_by_preference, [slotIndex]: nextList },
      };
    });
  };

  const stepRoomRequestCount = (slotIndex, roomTypeId, delta) => {
    const nextCount = Math.max(0, (Number(getRoomRequestCount(slotIndex, roomTypeId)) || 0) + delta);
    setRoomRequestCount(slotIndex, roomTypeId, nextCount);
  };

  const toggleRoomTypesPanel = (slotIndex) => {
    setRoomTypesPanelOpen((current) => ({ ...current, [slotIndex]: !current[slotIndex] }));
  };

  const getRoomTypeCapacityPriceText = (roomType) => {
    const capacityText = `Capacity: ${roomType.capacity} guest${roomType.capacity === 1 ? "" : "s"}`;
    return roomType.base_price ? `${capacityText} · ₹${roomType.base_price}/night` : capacityText;
  };

  const validate = () => {
    const e = {};
    if (!form.employee_id.trim()) e.employee_id = REQUIRED_MSG;
    else if (!EMPLOYEE_ID_RE.test(form.employee_id))
      e.employee_id = "Employee ID must be 5-6 digits (numbers only)";
    if (!form.applicant_name.trim()) e.applicant_name = REQUIRED_MSG;
    else if (!ALPHA_RE.test(form.applicant_name.trim()))
      e.applicant_name = "Only letters are allowed";
    if (!form.designation.trim()) e.designation = REQUIRED_MSG;
    else if (!ALPHA_RE.test(form.designation.trim()))
      e.designation = "Only letters are allowed";
    if (!form.department.trim()) e.department = REQUIRED_MSG;
    else if (!ALPHA_RE.test(form.department.trim()))
      e.department = "Only letters are allowed";
    if (!form.email.trim()) e.email = REQUIRED_MSG;
    else if (!EMAIL_RE.test(form.email.trim()))
      e.email = "Enter a valid email address";
    if (!form.mobile.trim()) e.mobile = REQUIRED_MSG;
    else if (!MOBILE_RE.test(form.mobile))
      e.mobile = "Mobile number must be exactly 10 digits";
    if (!form.guest_name.trim()) e.guest_name = REQUIRED_MSG;
    if (!form.guest_mobile.trim()) e.guest_mobile = REQUIRED_MSG;
    else if (!MOBILE_RE.test(form.guest_mobile))
      e.guest_mobile = "Mobile number must be exactly 10 digits";
    if (!form.purpose_of_visit.trim()) e.purpose_of_visit = REQUIRED_MSG;
    if (!form.rooms_required) e.rooms_required = REQUIRED_MSG;
    if (!form.payment_responsibility) e.payment_responsibility = REQUIRED_MSG;
    else if (form.payment_responsibility === OTHER_PAYMENT_CODE && !form.payment_responsibility_other.trim())
      e.payment_responsibility_other = REQUIRED_MSG;
    /* Room-type breakdown is an optional preference for admin-created
       bookings — any unassigned rooms are handled later during room
       allotment, so no total-must-match-rooms_required check here. */
    if (!form.arrival_date) e.arrival_date = REQUIRED_MSG;
    if (!form.arrival_time) e.arrival_time = REQUIRED_MSG;
    if (!form.departure_date) e.departure_date = REQUIRED_MSG;
    if (!form.departure_time) e.departure_time = REQUIRED_MSG;
    /* Same-day (day-use) stays are allowed; when the dates match, the times
       are the only thing separating arrival from departure. */
    if (form.arrival_date && form.departure_date) {
      if (form.departure_date < form.arrival_date) {
        e.departure_date = "Cannot be before arrival date";
      } else if (
        form.departure_date === form.arrival_date &&
        form.arrival_time &&
        form.departure_time &&
        form.departure_time <= form.arrival_time
      ) {
        e.departure_time = "Must be after arrival time for a same-day booking";
      }
    }
    if (!form.preferences.some((p) => p.guest_house))
      e.preferences = REQUIRED_MSG;
    if (form.booking_type === "OFFICIAL" && !form.official_attachment)
      e.official_attachment = REQUIRED_MSG;
    else if (form.official_attachment && form.official_attachment.size > MAX_ATTACHMENT_SIZE)
      e.official_attachment = ATTACHMENT_TOO_LARGE_MSG;
    return e;
  };

  /* "Submit Booking" only validates and asks for confirmation — the actual
     API call happens in doSubmit(), once the user confirms in the dialog. */
  const handleSubmitClick = () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const hasBlank = Object.values(errs).some((v) => v === REQUIRED_MSG);
      setNotice(
        hasBlank
          ? { type: "error", msg: "Please fill all mandatory fields before submitting." }
          : null,
      );
      setActiveStep(
        Math.min(...Object.keys(errs).map((k) => FIELD_STEP[k] ?? 0)),
      );
      return;
    }
    setConfirmSubmitOpen(true);
  };

  const doSubmit = async () => {
    setConfirmSubmitOpen(false);

    const payload = {
      employee_id: form.employee_id,
      applicant_name: form.applicant_name,
      designation: form.designation,
      department: form.department,
      email: form.email,
      mobile: form.mobile,
      guest_name: form.guest_name,
      guest_designation: form.guest_designation,
      guest_mobile: form.guest_mobile,
      guest_address: form.guest_address,
      booking_type: form.booking_type,
      purpose_of_visit: form.purpose_of_visit,
      accompanying_persons_count: Number(form.accompanying_persons_count) || 0,
      arrival_date: form.arrival_date,
      arrival_time: form.arrival_time + ":00",
      departure_date: form.departure_date,
      departure_time: form.departure_time + ":00",
      rooms_required: Number(form.rooms_required) || 1,
      payment_responsibility: form.payment_responsibility,
      payment_responsibility_other: form.payment_responsibility_other,
      preferences: form.preferences
        .map((p, j) => ({ ...p, _slotIndex: j }))
        .filter((p) => p.guest_house)
        .map((p, i) => ({
          guest_house: p.guest_house,
          priority: i + 1,
          room_requests: (form.room_requests_by_preference[p._slotIndex] || [])
            .filter((r) => r.roomTypeId && Number(r.count) >= 1)
            .map((r) => ({ room_type: Number(r.roomTypeId), count: Number(r.count) })),
        })),
    };

    setSubmitting(true);
    setErrors({});
    try {
      if (form.booking_type === "OFFICIAL" && form.official_attachment) {
        const fd = appendBookingPayload(new FormData(), payload);
        fd.append("official_attachment", form.official_attachment);

        await bookingApi.create(fd);
      } else {
        await bookingApi.create(JSON.stringify(payload));
      }
      setNotice({ type: "success", msg: "Your booking form has been submitted." });
      setForm(EMPTY_CB);
      setActiveStep(0);
      onSuccess?.();
    } catch (err) {
      let msg = "Failed to create booking.";
      try {
        const parsed = JSON.parse(err.message);
        if (parsed.detail) {
          msg = parsed.detail;
        } else if (parsed.preferences) {
          const prefErrs = Array.isArray(parsed.preferences)
            ? parsed.preferences.flatMap((e) =>
                typeof e === "string" ? [e] : Object.values(e).flat(),
              )
            : [JSON.stringify(parsed.preferences)];
          const prefMsg = prefErrs.filter(Boolean).join("; ");
          setErrors((prev) => ({ ...prev, preferences: prefMsg || "Invalid preferences." }));
          msg = prefMsg || "Invalid preferences. Please review and fix them.";
        } else {
          msg = Object.entries(parsed)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join("; ");
        }
      } catch {
        msg = err.message || "Failed to create booking.";
      }
      setNotice({ type: "error", msg });
    } finally {
      setSubmitting(false);
    }
  };

  const usedGhIds = form.preferences.map((p) => p.guest_house).filter(Boolean);

  const guestHouseOptionsFor = (currentGhId) =>
    guestHouses
      .filter((gh) => !usedGhIds.includes(gh.id) || gh.id === currentGhId)
      .map((gh) => ({ key: gh.id, text: gh.name, value: gh.id }));

  /* Show the multi-preference dropdown whenever there's actually more than
     one guest house to choose from — super admins always have several, and
     a GH_ADMIN assigned to more than one property needs the same picker,
     just capped to how many properties they actually have. */
  const showPreferenceDropdown = isSuperAdmin || guestHouses.length > 1;
  const preferenceSlotCount = isSuperAdmin
    ? form.preferences.length
    : Math.min(form.preferences.length, guestHouses.length);

  const currentPaymentResponsibilityOptions = (paymentResponsibilityOptions || [])
    .filter((o) => o.booking_type === form.booking_type && o.is_active)
    .map((o) => ({ key: o.code, text: o.label, value: o.code }));

  /* Display values for the Preview Form modal */
  const previewPreferences = form.preferences
    .filter((p) => p.guest_house)
    .map((p) => guestHouses.find((g) => Number(g.id) === Number(p.guest_house))?.name)
    .filter(Boolean)
    .join(", ");
  const previewPayment =
    currentPaymentResponsibilityOptions.find((o) => o.value === form.payment_responsibility)?.text || "";
  const previewAccompanying = Number(form.accompanying_persons_count) || 0;
  const previewRoomRequests = form.preferences
    .map((p, i) => {
      if (!p.guest_house) return null;
      const reqs = (form.room_requests_by_preference[i] || []).filter(
        (r) => r.roomTypeId && Number(r.count) >= 1,
      );
      if (!reqs.length) return null;
      const ghName = guestHouses.find((g) => Number(g.id) === Number(p.guest_house))?.name || "";
      const types = roomTypesByGh[p.guest_house] || [];
      const parts = reqs
        .map((r) => `${r.count} x ${types.find((t) => t.id === r.roomTypeId)?.name || "Room type"}`)
        .join(", ");
      return `${ghName}: ${parts}`;
    })
    .filter(Boolean)
    .join(" | ");

  /* Error icon on a step tab only reflects errors already surfaced by a
     Submit Booking attempt (the `errors` state) — not live validation — so
     steps aren't flagged red before the user has tried to submit. Fixing a
     field clears its entry via set(), so the icon updates as the user
     corrects things. */
  const stepHasErrors = (s) => stepFieldKeys(s).some((k) => errors[k]);

  /* Rendered inside the active section's white Segment card (not floated
     above it) so it reads as part of the form rather than a separate box. */
  const noticeBanner = notice && (
    <div
      style={{
        color: notice.type === "success" ? "#2c662d" : "#9f3a38",
        marginBottom: 14,
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
      }}
    >
      <Icon
        name={notice.type === "success" ? "check circle" : "exclamation triangle"}
      />
      <span>{notice.msg}</span>
      <Icon
        name="close"
        link
        onClick={() => setNotice(null)}
        style={{ marginLeft: "auto" }}
        aria-label="Dismiss notice"
      />
    </div>
  );

  return (
    <>
      <Step.Group widths={4} fluid size="small" style={{ marginBottom: 12 }}>
        {STEPS.map((label, index) => {
          const isActive = activeStep === index;
          const hasError = stepHasErrors(index);
          return (
            <Step
              key={label}
              link
              active={isActive}
              onClick={() => goToStep(index)}
              style={{
                padding: "10px 18px",
                minHeight: 0,
                ...(isActive ? { backgroundColor: "#2185d0", color: "#fff" } : {}),
              }}
            >
              <Label
                circular
                style={{
                  marginRight: 10,
                  backgroundColor: isActive ? "#fff" : "#2185d0",
                  color: isActive ? "#2185d0" : "#fff",
                }}
              >
                {index + 1}
              </Label>
              <Step.Content>
                <Step.Title style={isActive ? { color: "#fff" } : undefined}>
                  {label}
                  {hasError && (
                    <Icon
                      name="exclamation circle"
                      style={{ color: isActive ? "#fff" : "#db2828", marginLeft: 8 }}
                      title="Mandatory field missing or invalid"
                    />
                  )}
                </Step.Title>
              </Step.Content>
            </Step>
          );
        })}
      </Step.Group>

      <Segment.Group>
        {/* Section 1 — Applicant */}
        {activeStep === 0 && (
        <Segment>
          {noticeBanner}
          <Header as="h5" icon="id card outline" content="Applicant Details" />
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                label={inlineLabel("Employee ID", errors.employee_id)}
                placeholder="5-6 digit number"
                maxLength={6}
                value={form.employee_id}
                onChange={(e) => set("employee_id", digitsOnly(e.target.value))}
              />
              <Form.Input
                label={inlineLabel("Applicant Name", errors.applicant_name)}
                placeholder="Full name"
                value={form.applicant_name}
                onChange={(e) => set("applicant_name", lettersOnly(e.target.value))}
              />
              <Form.Input
                label={inlineLabel("Designation", errors.designation)}
                placeholder="e.g. Professor"
                value={form.designation}
                onChange={(e) => set("designation", lettersOnly(e.target.value))}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label={inlineLabel("Department", errors.department)}
                placeholder="e.g. Computer Science"
                value={form.department}
                onChange={(e) => set("department", lettersOnly(e.target.value))}
              />
              <Form.Input
                label={inlineLabel("Email ID", errors.email)}
                type="email"
                placeholder="email@example.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
              <Form.Input
                label={inlineLabel("Mobile No.", errors.mobile)}
                placeholder="10-digit number"
                maxLength={10}
                value={form.mobile}
                onChange={(e) => set("mobile", digitsOnly(e.target.value))}
              />
            </Form.Group>
          </Form>
          {EMPLOYEE_ID_RE.test(debouncedEmployeeId) && (
            <div style={{ marginTop: 16 }}>
              <Header as="h6" content="Previous bookings for this employee" style={{ marginBottom: 8 }} />
              {employeeHistoryLoading ? (
                <Loader active inline size="small" content="Checking previous bookings…" />
              ) : employeeHistoryError ? (
                <Message negative size="small" content={employeeHistoryError} />
              ) : employeeHistory.length === 0 ? (
                <Message size="small" content="No previous bookings for this employee." />
              ) : (
                <Table celled compact size="small" selectable style={{ marginTop: 0 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Booking No</Table.HeaderCell>
                      <Table.HeaderCell>Guest Name</Table.HeaderCell>
                      <Table.HeaderCell>Department</Table.HeaderCell>
                      <Table.HeaderCell>Type</Table.HeaderCell>
                      <Table.HeaderCell>Guest House</Table.HeaderCell>
                      <Table.HeaderCell>Arrival</Table.HeaderCell>
                      <Table.HeaderCell>Departure</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Nights</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Rooms</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {employeeHistory.map((row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell>{row.bookingCode}</Table.Cell>
                        <Table.Cell>{row.guestName || "—"}</Table.Cell>
                        <Table.Cell>{row.department || "—"}</Table.Cell>
                        <Table.Cell>{row.bookingType}</Table.Cell>
                        <Table.Cell>{row.guestHouseShortName || row.guestHouseName || "—"}</Table.Cell>
                        <Table.Cell>{fmt(row.checkIn)}</Table.Cell>
                        <Table.Cell>{fmt(row.checkOut)}</Table.Cell>
                        <Table.Cell textAlign="center">{row.numberOfDays ?? "—"}</Table.Cell>
                        <Table.Cell textAlign="center">{row.roomsRequired ?? "—"}</Table.Cell>
                        <Table.Cell><StatusBadge status={row.status} /></Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          )}
        </Segment>
        )}

        {/* Section 2 — Guest */}
        {activeStep === 1 && (
        <Segment>
          {noticeBanner}
          <Header as="h5" icon="info circle outline" content="Visit Details" />
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                label={inlineLabel("Purpose of Visit", errors.purpose_of_visit)}
                placeholder="e.g. Academic Conference"
                value={form.purpose_of_visit}
                onChange={(e) => set("purpose_of_visit", e.target.value)}
              />
              <Form.Select
                label="Booking Type"
                required
                options={BOOKING_TYPE_OPTIONS}
                value={form.booking_type}
                onChange={(_, data) => {
                  set("booking_type", data.value);
                  if (data.value === "NON_OFFICIAL") set("official_attachment", null);
                  /* The valid Payment Responsibility options differ per booking
                     type — clear any stale selection from the other type. */
                  set("payment_responsibility", "");
                  set("payment_responsibility_other", "");
                }}
              />
              <Form.Select
                label={inlineLabel("Payment Responsibility", errors.payment_responsibility)}
                options={currentPaymentResponsibilityOptions}
                value={form.payment_responsibility}
                onChange={(_, data) => {
                  set("payment_responsibility", data.value);
                  if (data.value !== OTHER_PAYMENT_CODE) set("payment_responsibility_other", "");
                }}
              />
              {form.payment_responsibility === OTHER_PAYMENT_CODE && (
                <Form.Input
                  label={inlineLabel("Please Specify", errors.payment_responsibility_other)}
                  placeholder="Who is responsible for payment?"
                  value={form.payment_responsibility_other}
                  onChange={(e) => set("payment_responsibility_other", e.target.value)}
                />
              )}
              {form.booking_type === "OFFICIAL" && (
                <Form.Field>
                  {inlineLabel("Official Document", errors.official_attachment)}
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 8 }}>
                    <Button
                      as="label"
                      htmlFor="official-attachment-input"
                      basic
                      type="button"
                      style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                    >
                      <Icon name="upload" />
                      {form.official_attachment
                        ? form.official_attachment.name
                        : "Choose file…"}
                    </Button>
                    {form.official_attachment && (
                      <Button
                        basic
                        type="button"
                        icon="eye"
                        content="Preview"
                        style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                        onClick={() => setPreviewOpen(true)}
                      />
                    )}
                  </div>
                  <input
                    id="official-attachment-input"
                    type="file"
                    hidden
                    accept={OFFICIAL_ATTACHMENT_ACCEPT}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > MAX_ATTACHMENT_SIZE) {
                        e.target.value = "";
                        set("official_attachment", null);
                        setErrors((prev) => ({
                          ...prev,
                          official_attachment: ATTACHMENT_TOO_LARGE_MSG,
                        }));
                        return;
                      }
                      set("official_attachment", file);
                    }}
                  />
                </Form.Field>
              )}
            </Form.Group>
            <p style={{ fontSize: "0.78rem", color: "rgba(0,0,0,0.5)", marginTop: -8 }}>
              <em>
                Note: for Official booking type, you must upload a valid official document.
              </em>
            </p>
          </Form>

          <Divider />

          <Header as="h5" icon="user outline" content="Guest Information" />
          <Form>
            <Form.Field>
              <Checkbox
                label="Guest information same as applicant"
                checked={form.guest_same_as_applicant}
                onChange={(_, data) => set("guest_same_as_applicant", !!data.checked)}
              />
            </Form.Field>
            <Form.Group widths="equal">
              <Form.Input
                label={inlineLabel("Guest Name", errors.guest_name)}
                placeholder="Guest full name"
                readOnly={form.guest_same_as_applicant}
                input={form.guest_same_as_applicant ? { style: GREYED_INPUT_STYLE } : undefined}
                value={form.guest_name}
                onChange={(e) => set("guest_name", e.target.value)}
              />
              <Form.Input
                label={inlineLabel("Guest Designation", errors.guest_designation, false)}
                placeholder="e.g. Dean"
                readOnly={form.guest_same_as_applicant}
                input={form.guest_same_as_applicant ? { style: GREYED_INPUT_STYLE } : undefined}
                value={form.guest_designation}
                onChange={(e) => set("guest_designation", e.target.value)}
              />
              <Form.Input
                label={inlineLabel("Guest Mobile", errors.guest_mobile)}
                placeholder="10-digit number"
                maxLength={10}
                readOnly={form.guest_same_as_applicant}
                input={form.guest_same_as_applicant ? { style: GREYED_INPUT_STYLE } : undefined}
                value={form.guest_mobile}
                onChange={(e) => set("guest_mobile", digitsOnly(e.target.value))}
              />
              <Form.Input
                label={inlineLabel("Guest Address", errors.guest_address, false)}
                placeholder="Full address"
                value={form.guest_address}
                onChange={(e) => set("guest_address", e.target.value)}
              />
              <Form.Input
                label="No. of Accompanying Persons"
                icon="user"
                iconPosition="left"
                type="number"
                min="0"
                placeholder="0"
                value={form.accompanying_persons_count}
                onChange={(e) => set("accompanying_persons_count", e.target.value)}
              />
            </Form.Group>
          </Form>
        </Segment>
        )}

        {/* Section 3 — Accommodation & Stay */}
        {activeStep === 2 && (
        <Segment>
          {noticeBanner}
          <Header as="h5" icon="building outline" content="Accommodation Details" />
          <Form>
            <Form.Group>
              <Form.Input
                label={inlineLabel("Rooms Required", errors.rooms_required)}
                type="number"
                min="1"
                max="20"
                width={5}
                value={form.rooms_required}
                onChange={(e) => set("rooms_required", e.target.value)}
              />
            </Form.Group>

            <Divider />

            {showPreferenceDropdown ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                {form.preferences.slice(0, preferenceSlotCount).map((p, i) => (
                  <Form.Select
                    key={i}
                    label={inlineLabel(`Preference ${i + 1}`, i === 0 ? errors.preferences : undefined, i === 0)}
                    clearable
                    search
                    placeholder="Select Guest House"
                    options={guestHouseOptionsFor(p.guest_house)}
                    value={p.guest_house}
                    onChange={(_, data) => setPref(i, data.value)}
                  />
                ))}
              </div>
            ) : (
              <Form.Group>
                <Form.Input
                  label="Guest House"
                  width={5}
                  value={guestHouses[0]?.name || ""}
                  readOnly
                />
              </Form.Group>
            )}

            <div
              style={
                showPreferenceDropdown
                  ? { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }
                  : undefined
              }
            >
            {(showPreferenceDropdown
              ? form.preferences.slice(0, preferenceSlotCount).map((p, i) => [p.guest_house, i])
              : [[guestHouses[0]?.id, 0]]
            ).map(([ghId, slotIndex]) => {
              if (!ghId) return <div key={`rt-${slotIndex}`} />;
              const label =
                guestHouses.find((g) => Number(g.id) === Number(ghId))?.name ||
                `Preference ${slotIndex + 1}`;
              const types = roomTypesByGh[ghId];
              const isPanelOpen = Boolean(roomTypesPanelOpen[slotIndex]);
              const selectedSummary = (form.room_requests_by_preference[slotIndex] || [])
                .filter((r) => Number(r.count) > 0)
                .map((r) => {
                  const roomTypeName = (types || []).find((t) => t.id === r.roomTypeId)?.name;
                  return roomTypeName ? `${r.count} ${roomTypeName}` : null;
                })
                .filter(Boolean)
                .join(", ");

              return (
                <div key={`rt-${slotIndex}`} style={{ margin: "4px 0 14px", maxWidth: showPreferenceDropdown ? undefined : 320 }}>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "rgba(0,0,0,0.4)",
                      marginBottom: 6,
                    }}
                  >
                    Room types for {label}
                  </div>

                  <div style={{ border: "1px solid rgba(34,36,38,.15)", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                    <button
                      type="button"
                      onClick={() => toggleRoomTypesPanel(slotIndex)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1rem",
                        color: "#143a5a",
                        textAlign: "left",
                      }}
                    >
                      <span>{selectedSummary || "Select room types"}</span>
                      <Icon name={isPanelOpen ? "chevron up" : "chevron down"} />
                    </button>

                    {isPanelOpen && (
                      <div style={{ borderTop: "1px solid rgba(34,36,38,.1)" }}>
                        {types === undefined ? (
                          <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                            <Icon name="spinner" loading /> Loading room types…
                          </p>
                        ) : types.length === 0 ? (
                          <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                            <em>No room types configured for this guest house.</em>
                          </p>
                        ) : (
                          types.map((rt, rtIndex) => {
                            const count = Number(getRoomRequestCount(slotIndex, rt.id)) || 0;
                            return (
                              <div
                                key={rt.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "14px 16px",
                                  borderTop: rtIndex > 0 ? "1px solid rgba(34,36,38,.08)" : "none",
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 700 }}>{rt.name}</div>
                                  <div style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.55)" }}>
                                    {getRoomTypeCapacityPriceText(rt)}
                                  </div>
                                  {rt.description?.trim() && (
                                    <div style={{ fontSize: "0.78rem", color: "rgba(0,0,0,0.45)", fontStyle: "italic", marginTop: 2 }}>
                                      {rt.description}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                  <Button
                                    circular
                                    basic
                                    icon="minus"
                                    size="mini"
                                    type="button"
                                    disabled={count <= 0}
                                    aria-label={`Decrease ${rt.name} count`}
                                    onClick={() => stepRoomRequestCount(slotIndex, rt.id, -1)}
                                  />
                                  <span style={{ minWidth: 18, textAlign: "center" }}>{count}</span>
                                  <Button
                                    circular
                                    color="blue"
                                    icon="plus"
                                    size="mini"
                                    type="button"
                                    disabled={getSlotRoomTotal(slotIndex) >= (Number(form.rooms_required) || 0)}
                                    aria-label={`Increase ${rt.name} count`}
                                    onClick={() => stepRoomRequestCount(slotIndex, rt.id, 1)}
                                  />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </Form>

          <Header as="h5" icon="calendar outline" content="Stay Details" style={{ marginTop: 24 }} />
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                label={inlineLabel("Arrival Date", errors.arrival_date)}
                type="date"
                value={form.arrival_date}
                onChange={(e) => set("arrival_date", e.target.value)}
              />
              <TimeInput12h
                label={inlineLabel("Arrival Time", errors.arrival_time)}
                value={form.arrival_time}
                onChange={(v) => set("arrival_time", v)}
                error={Boolean(errors.arrival_time)}
              />
              <Form.Input
                label={inlineLabel("Departure Date", errors.departure_date)}
                type="date"
                min={form.arrival_date || undefined}
                value={form.departure_date}
                onChange={(e) => set("departure_date", e.target.value)}
              />
              <TimeInput12h
                label={inlineLabel("Departure Time", errors.departure_time)}
                value={form.departure_time}
                onChange={(v) => set("departure_time", v)}
                error={Boolean(errors.departure_time)}
              />
            </Form.Group>
          </Form>
        </Segment>
        )}

        {/* Actions */}
        <Segment secondary textAlign="right">
          <Button
            basic
            type="button"
            icon="refresh"
            content="Reset"
            onClick={resetCurrentStep}
          />
          {activeStep > 0 && (
            <Button basic type="button" onClick={goBack}>
              <Icon name="arrow left" /> Back
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button primary type="button" onClick={goNext}>
              Next <Icon name="arrow right" />
            </Button>
          ) : (
            <Button
              primary
              type="button"
              loading={submitting}
              disabled={submitting}
              icon={submitting ? undefined : "paper plane outline"}
              content={submitting ? "Submitting…" : "Submit Booking"}
              onClick={handleSubmitClick}
            />
          )}
        </Segment>
      </Segment.Group>

      {/* Attachment preview — lets the user check the file before submitting */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} size="large" closeIcon>
        <Modal.Header>
          <Icon name="file outline" />
          {form.official_attachment?.name}
        </Modal.Header>
        <Modal.Content scrolling>
          {form.official_attachment?.type.startsWith("image/") ? (
            <img
              src={attachmentPreviewUrl}
              alt={form.official_attachment.name}
              style={{ maxWidth: "100%" }}
            />
          ) : form.official_attachment?.type === "application/pdf" ? (
            <iframe
              src={attachmentPreviewUrl}
              title={form.official_attachment.name}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ) : (
            <p>
              Preview isn't available for this file type.{" "}
              <a href={attachmentPreviewUrl} target="_blank" rel="noreferrer">
                Open the file
              </a>{" "}
              to check it before submitting.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Confirm submission — shows a read-only preview of everything filled
          in, then asks the user to confirm before actually submitting. */}
      <Modal open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} size="large" closeIcon>
        <Modal.Header>
          <Icon name="eye" />
          Preview Booking
        </Modal.Header>
        <Modal.Content scrolling>
          <Header as="h5" icon="id card outline" content="Applicant Details" />
          <Grid columns={3} stackable>
            <PF label="Employee ID" value={form.employee_id} />
            <PF label="Applicant Name" value={form.applicant_name} />
            <PF label="Designation" value={form.designation} />
          </Grid>
          <Grid columns={3} stackable style={{ marginTop: 4 }}>
            <PF label="Department" value={form.department} />
            <PF label="Email ID" value={form.email} />
            <PF label="Mobile No." value={form.mobile} />
          </Grid>

          <Divider />
          <Header as="h5" icon="user outline" content="Guest Information" />
          <Grid columns={3} stackable>
            <PF label="Guest Name" value={form.guest_name} />
            <PF label="Guest Designation" value={form.guest_designation} />
            <PF label="Guest Mobile" value={form.guest_mobile} />
          </Grid>
          <Grid columns={3} stackable style={{ marginTop: 4 }}>
            <PF label="Guest Address" value={form.guest_address} />
            <PF label="Purpose of Visit" value={form.purpose_of_visit} />
            <PF
              label="Booking Type"
              value={form.booking_type === "OFFICIAL" ? "Official" : "Non-official"}
            />
          </Grid>
          {form.booking_type === "OFFICIAL" && (
            <Grid columns={3} stackable style={{ marginTop: 4 }}>
              <PF label="Official Document" value={form.official_attachment?.name} />
            </Grid>
          )}
          {previewAccompanying && (
            <Grid columns={1} stackable style={{ marginTop: 4 }}>
              <PF label="Accompanying Persons" value={previewAccompanying} />
            </Grid>
          )}

          <Divider />
          <Header as="h5" icon="building outline" content="Accommodation & Stay" />
          <Grid columns={3} stackable>
            <PF label="Guest House Preferences" value={previewPreferences} />
            <PF label="Rooms Required" value={form.rooms_required} />
            {form.booking_type !== "OFFICIAL" && (
              <PF label="Payment Responsibility" value={previewPayment} />
            )}
            {form.payment_responsibility === OTHER_PAYMENT_CODE && (
              <PF label="Please Specify" value={form.payment_responsibility_other} />
            )}
          </Grid>
          {previewRoomRequests && (
            <Grid columns={1} stackable style={{ marginTop: 4 }}>
              <PF label="Room Type Breakdown" value={previewRoomRequests} />
            </Grid>
          )}
          <Grid columns={4} stackable style={{ marginTop: 4 }}>
            <PF label="Arrival Date" value={fmt(form.arrival_date)} />
            <PF label="Arrival Time" value={form.arrival_time} />
            <PF label="Departure Date" value={fmt(form.departure_date)} />
            <PF label="Departure Time" value={form.departure_time} />
          </Grid>

          <Divider />
          <p style={{ fontWeight: 600 }}>Are you sure you want to submit this booking?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setConfirmSubmitOpen(false)}>
            Cancel
          </Button>
          <Button primary loading={submitting} disabled={submitting} onClick={doSubmit}>
            Yes
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
